// One collapsible filter bar, shared by every screen that has filters.
//
// Luyanda, on a phone: "please make the filter and various blocks collapsable
// for viewing most things on the owner statement tab, performance, and any
// other tab with a filter."
//
// The problem this solves is specific. A filter row that is four controls wide
// on a desk is four controls TALL on a phone, and on the owner statement month
// view that pushed the actual figures off the bottom of the screen. The filters
// are how you get to the thing; they are not the thing.
//
// Three rules this component follows, and one it deliberately does not:
//
//  1. NOTHING CHANGES ON A DESKTOP. At sm and above the bar renders exactly as
//     it did: no header, no button, no wrapper chrome. A person on a laptop
//     should not be able to tell this component was added.
//
//  2. ON A PHONE IT STARTS CLOSED, BUT ONLY WHEN IT IS EMPTY. If a filter is
//     already set, the bar opens, because a screen showing a filtered subset
//     while hiding the filter that caused it is a screen that lies. `activeCount`
//     is what decides this, and it is the caller's job to count honestly.
//
//  3. THE SUMMARY SAYS WHAT IS ON. Collapsed, the button reads "Filters" or
//     "Filters (2)", and `summary` can carry the specifics, so the state is
//     legible without opening anything.
//
// What it does NOT do: remember the open state between visits. A filter panel
// that reopens itself because you left it open last Tuesday is worse than one
// that starts from a known position every time.
import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'

type Props = {
  children: ReactNode
  // How many filters are currently narrowing the view. Drives both the badge
  // and whether the bar opens itself on a phone. Count only the ones that
  // actually change what is shown: a default month or an "All" is not a filter.
  activeCount?: number
  // Optional one-line description of the active filters, shown next to the
  // button while collapsed.
  summary?: string
  // Shown on the collapsed row, right-aligned. For a count of what is on screen,
  // so the number survives the filters being hidden.
  trailing?: ReactNode
  label?: string
  className?: string
}

export function FilterBar({
  children,
  activeCount = 0,
  summary,
  trailing,
  label = 'Filters',
  className,
}: Props) {
  const [open, setOpen] = useState(activeCount > 0)

  // A filter applied from somewhere else, a deep link or a cleared set, moves
  // the bar with it rather than leaving a stale panel behind.
  useEffect(() => {
    if (activeCount > 0) setOpen(true)
  }, [activeCount])

  return (
    <div className={cn('border-b border-border bg-card', className)}>
      {/* Phone only. Hidden from the accessibility tree and the layout at sm+,
          so a desktop DOM is unchanged. */}
      <div className="flex items-center gap-2 px-4 py-2 sm:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          {label}
          {activeCount > 0 && (
            <span className="rounded bg-[hsl(var(--brand-soft))] px-1.5 py-0.5 text-[10px] font-semibold text-[hsl(var(--brand-soft-foreground))]">
              {activeCount}
            </span>
          )}
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        </button>
        {!open && summary && (
          <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">{summary}</span>
        )}
        {trailing && <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{trailing}</span>}
      </div>

      <div className={cn(open ? 'block' : 'hidden', 'sm:block')}>{children}</div>
    </div>
  )
}

// The same idea for a block of content that is not a filter: a summary strip, a
// chart, a panel of chips. Collapsed on a phone, always open at sm and above.
//
// `defaultOpen` exists because some blocks are the point of the screen and some
// are context. A KPI strip is context on a phone; the table under it is not.
export function CollapsibleBlock({
  title,
  children,
  defaultOpen = false,
  trailing,
  className,
}: {
  title: string
  children: ReactNode
  defaultOpen?: boolean
  trailing?: ReactNode
  className?: string
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:hidden"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        {title}
        {trailing && <span className="ml-auto font-normal normal-case">{trailing}</span>}
      </button>
      <div className={cn(open ? 'block' : 'hidden', 'sm:block')}>{children}</div>
    </div>
  )
}
